import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Chip,
  CircularProgress, // 1. Import adicionado
} from '@mui/material';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useParams } from 'react-router-dom';
// import { useCuffaStore } from 'stores/cuffa/cuffa.store'; // Removido
import { CuffaAPI } from 'api/cuffa/cuffa.api'; // 1. Import da API adicionado
import { CuffaValueAddedSealEnum } from 'interfaces/cuffa.interface';
import { ROUTES } from 'routes/constants';

const selosPadrao = Object.values(CuffaValueAddedSealEnum).filter(
  (selo) => selo !== CuffaValueAddedSealEnum.OUTRO
);

interface ISelosAgregacaoValorData {
  possuiSelo: string;
  selosSelecionados: string[];
  outrosSelos: string[];
  nomesSelos: Record<string, string>;
}

export const SelosAgregacaoValorVisualizar: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();

  // 2. Estado de 'loading' adicionado e 'useCuffaStore' removido
  const [dados, setDados] = useState<ISelosAgregacaoValorData | null>(null);
  const [loading, setLoading] = useState(true);

  // 3. useEffect reescrito para buscar dados da API
  useEffect(() => {
    const fetchSealsData = async () => {
      if (!uuid) {
        setLoading(false);
        return;
      }
      // Tenta restaurar dos storages: primeiro específico por uuid, depois o global
      try {
        const storageKeys: string[] = [];
        if (uuid) storageKeys.push(`cuffa-storage-${uuid}`);
        storageKeys.push('cuffa-storage');
        for (const storageKey of storageKeys) {
          try {
            const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
            if (!raw) continue;
            const parsed = JSON.parse(raw);
            const saved = parsed?.state?.dadosParciais?.selosAgregacaoValor;
            if (saved) {
              const mappedData: ISelosAgregacaoValorData = {
                possuiSelo: saved.possuiSelo ?? (saved.has_value_added_seals ? 'sim' : 'não'),
                selosSelecionados: Array.isArray(saved.selosSelecionados) ? saved.selosSelecionados : (saved.value_added_seals || []),
                outrosSelos: Array.isArray(saved.outrosSelos) ? saved.outrosSelos : (Array.isArray(saved.other_value_added_seals) ? saved.other_value_added_seals : Object.keys(saved.other_value_added_seals || {})),
                nomesSelos: saved.nomesSelos || saved.other_value_added_seals || {},
              };
              setDados(mappedData);
              setLoading(false);
              return;
            }
          } catch (e) {
            console.warn(`Falha ao parsear ${storageKey} ao restaurar selosAgregacaoValor:`, e);
            continue;
          }
        }
      } catch (e) {
        console.warn('Erro ao acessar localStorage cuffa-storage ao restaurar selosAgregacaoValor', e);
      }
      try {
        const response = await CuffaAPI.getDataByID(uuid);

        if (response && response.dados) {
          const apiData = response.dados;

          const mappedData: ISelosAgregacaoValorData = {
            possuiSelo: apiData.has_value_added_seals ? 'sim' : 'não',
            selosSelecionados: apiData.value_added_seals || [],
            // O objeto other_value_added_seals da API contém os nomes dos selos "outros"
            outrosSelos: Object.keys(apiData.other_value_added_seals || {}),
            nomesSelos: apiData.other_value_added_seals || {},
          };
          setDados(mappedData);
        } else {
          console.error("Dados de selos não encontrados na resposta da API:", response);
          setDados(null);
        }
      } catch (error) {
        console.error("Erro ao buscar dados de selos:", error);
        setDados(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSealsData();
  }, [uuid]);

  // 4. Adicionados blocos para exibir o status de 'Carregando' e 'Erro'
  if (loading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Selos de Agregação de Valor" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  if (!dados) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Selos de Agregação de Valor" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" margin="2rem 0">
          <Typography>Não foi possível carregar os dados. Verifique o console para mais detalhes.</Typography>
        </Box>
      </MenuProvider>
    );
  }

  // O restante do código JSX continua o mesmo
  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Visualização dos Selos de Agregação de Valor" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Selos de Agregação de Valor" width="100%">
            {/* Seção: Possui selo? */}
            <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                A unidade familiar possui selo de agregação de valor atrelado à comercialização?
              </Typography>
              <Typography variant="body1">
                {dados.possuiSelo === 'sim' ? 'Sim' : 'Não'}
              </Typography>
            </Paper>

            {/* Seção: Selos (se aplicável) */}
            {dados.possuiSelo === 'sim' && (
              <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Selos de Agregação de Valor
                </Typography>

                {/* Selos padrão selecionados */}
                {dados.selosSelecionados.length > 0 && (
                  <Box mb={4}>
                    <Typography variant="subtitle1" gutterBottom>
                      Selos selecionados:
                    </Typography>
                    <Grid container spacing={1}>
                      {dados.selosSelecionados.map((selo, index) => (
                        <Grid item key={index}>
                          <Box>
                            <Chip label={selo} color="primary" variant="outlined" />
                            {dados.nomesSelos[selo] && (
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>Nome do selo:</strong> {dados.nomesSelos[selo]}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Outros selos */}
                {dados.outrosSelos.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Outros selos:
                    </Typography>
                    <List dense>
                      {dados.outrosSelos.map((selo, index) => (
                        <div key={index}>
                          <ListItem>
                            <ListItemText
                              primary={selo}
                              secondary={dados.nomesSelos[selo] ? `Nome do selo: ${dados.nomesSelos[selo]}` : null}
                            />
                          </ListItem>
                          {index < dados.outrosSelos.length - 1 && <Divider />}
                        </div>
                      ))}
                    </List>
                  </Box>
                )}

                {dados.selosSelecionados.length === 0 && dados.outrosSelos.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum selo registrado
                  </Typography>
                )}
              </Paper>
            )}

            <Box marginTop="2rem" display="flex" justifyContent="center" gap="1rem">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(ROUTES.INSPECAO_PRODUTOS_CUFFA_VISUALIZAR.replace(':uuid', uuid ?? ''))}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (uuid) {
                    navigate(ROUTES.CANAIS_COMERCIALIZACAO_CUFFA_VISUALIZAR.replace(':uuid', uuid));
                  }
                }}
              >
                Avançar
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>
    </MenuProvider>
  );
};

export default SelosAgregacaoValorVisualizar;
